// @ts-check

/**
 * @file Provides the reusable empty-state card.
 */
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Card from "./card"
import Text from "./text"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} EmptyStateProps
 * @property {string} message Message shown in the empty state.
 * @property {string} testID Stable test id.
 */

/** @typedef {Record<string, never>} EmptyStateState */

/**
 * Reusable empty-list card for screens.
 * @augments {ShapeComponent<EmptyStateProps, EmptyStateState>}
 */
class EmptyState extends ShapeComponent {
  static propTypes = propTypesExact({
    message: PropTypes.string.isRequired,
    testID: PropTypes.string.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the empty-state card. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the empty-state card.
   * @returns {ReactNode} Empty-state content.
   */
  render() {
    const {message, testID} = this.p
    return (
      <Card
        style={styles.cardContent ||= {
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 32
        }}
        testID={testID}
      >
        <Text
          style={styles[`text-${this.tt.themeName}`] ||= {
            color: this.tt.colors.textMuted,
            fontSize: 14,
            fontWeight: 600
          }}
          testID={`${testID}/text`}
        >
          {message}
        </Text>
      </Card>
    )
  }
}

/** @type {NamedExoticComponent<EmptyStateProps>} */
const EmptyStateComponent = memo(shapeComponent(EmptyState))

export default EmptyStateComponent
